// src/services/practiceAreaConfigService.ts
import type { PracticeAreaNumberingConfig, MatterNumberingTemplate } from '../types/matterNumbering';
import {
  getSystemPreferences,
  updateSystemPreferences,
  type OperatingContext,
  type ServiceArea,
  type SystemPreferences
} from '../features/admin/services/SystemPreferencesAPI';

// Service-area interface matching the inherited backend payload shape.
export interface PracticeArea {
  id: string;
  name: string;
  displayName: string;
  isActive: boolean;
}

// Subscriber settings interface backed by inherited firm-settings route names.
export interface FirmSettings {
  firmName: string;
  displayName: string;
  practiceAreas: string[];
  licensedStates?: string[];
  firmSettings: {
    timezone: string;
    dateFormat: string;
    currency: string;
    language: string;
    taskReminders?: unknown;
  };
  matterNumberingConfig?: {
    isEnabled: boolean;
    defaultTemplate: MatterNumberingTemplate;
    practiceAreaConfigs: PracticeAreaNumberingConfig[];
  };
}

// Compatibility service for service-area configuration management.
// Method and field names keep inherited practice-area wording until persistence is migrated.
export class PracticeAreaConfigService {
  private static instance: PracticeAreaConfigService;
  private cachedPracticeAreas: PracticeArea[] | null = null;
  private cachedFirmSettings: FirmSettings | null = null;
  private cacheExpiry = 5 * 60 * 1000; // 5 minutes
  private lastCacheTime = 0;

  private constructor() {}

  public static getInstance(): PracticeAreaConfigService {
    if (!PracticeAreaConfigService.instance) {
      PracticeAreaConfigService.instance = new PracticeAreaConfigService();
    }
    return PracticeAreaConfigService.instance;
  }

  /**
   * Get all available service areas from the backend.
   */
  async getAvailablePracticeAreas(): Promise<PracticeArea[]> {
    // Check cache first
    if (this.cachedPracticeAreas && Date.now() - this.lastCacheTime < this.cacheExpiry) {
      return this.cachedPracticeAreas;
    }

    try {
      const preferences = await getSystemPreferences();
      const practiceAreas = this.normalizePracticeAreas(
        (preferences.service_areas || []).filter((area) => area.enabled !== false)
      );
      const resolvedAreas = practiceAreas.length > 0 ? practiceAreas : this.getDefaultPracticeAreas();
      this.cachedPracticeAreas = resolvedAreas;
      this.lastCacheTime = Date.now();
      return resolvedAreas;
    } catch (error) {
      console.error('Error loading service areas:', error);
      // Return default service areas if API fails
      return this.getDefaultPracticeAreas();
    }
  }

  private normalizePracticeAreas(payload: unknown): PracticeArea[] {
    if (!Array.isArray(payload)) return [];

    const normalized: PracticeArea[] = [];
    const seen = new Set<string>();

    for (let i = 0; i < payload.length; i++) {
      const item = payload[i];

      let displayName = '';
      let name = '';
      let id = '';
      let isActive = true;

      if (typeof item === 'string') {
        displayName = item.trim();
        name = displayName;
      } else if (item && typeof item === 'object') {
        const candidate = item as Partial<PracticeArea> & { id?: string };
        displayName = String(candidate.displayName || candidate.name || '').trim();
        name = String(candidate.name || candidate.displayName || '').trim();
        isActive = candidate.isActive !== false;
        id = String(candidate.id || '').trim();
      }

      if (!displayName) continue;
      if (!name) name = displayName;
      if (!id) {
        id = name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '') || `practice-area-${i}`;
      }

      const dedupeKey = `${displayName.toLowerCase()}|${name.toLowerCase()}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);

      normalized.push({ id, name, displayName, isActive });
    }

    return normalized;
  }

  /**
   * Get subscriber settings including selected service areas.
   */
  async getFirmSettings(): Promise<FirmSettings> {
    // Check cache first
    if (this.cachedFirmSettings && Date.now() - this.lastCacheTime < this.cacheExpiry) {
      return this.cachedFirmSettings;
    }

    try {
      const [preferences, authContext] = await Promise.all([
        getSystemPreferences(),
        this.getAuthContext().catch(() => null)
      ]);
      const firmSettings = this.mapSystemPreferencesToFirmSettings(preferences, authContext);
      this.cachedFirmSettings = firmSettings;
      this.lastCacheTime = Date.now();
      return firmSettings;
    } catch (error) {
      console.error('Error loading subscriber settings:', error);
      throw error;
    }
  }

  /**
   * Get selected service areas for the current subscriber.
   */
  async getSelectedPracticeAreas(): Promise<string[]> {
    try {
      const firmSettings = await this.getFirmSettings();
      return firmSettings.practiceAreas || [];
    } catch (error) {
      console.error('Error getting selected service areas:', error);
      return [];
    }
  }

  /**
   * Check if a service area is selected for the current subscriber.
   */
  async isPracticeAreaSelected(practiceAreaName: string): Promise<boolean> {
    try {
      const selectedAreas = await this.getSelectedPracticeAreas();
      return selectedAreas.includes(practiceAreaName);
    } catch (error) {
      console.error('Error checking service area selection:', error);
      return false;
    }
  }

  /**
   * Get service area configuration for a specific service area.
   */
  async getPracticeAreaConfig(practiceArea: string): Promise<PracticeAreaNumberingConfig | null> {
    try {
      const firmSettings = await this.getFirmSettings();
      const config = firmSettings.matterNumberingConfig?.practiceAreaConfigs?.find(
        pa => pa.practiceArea === practiceArea
      );
      return config || null;
    } catch (error) {
      console.error('Error getting service area config:', error);
      return null;
    }
  }

  /**
   * Get all service area configurations for the current subscriber.
   */
  async getAllPracticeAreaConfigs(): Promise<PracticeAreaNumberingConfig[]> {
    try {
      const firmSettings = await this.getFirmSettings();
      return firmSettings.matterNumberingConfig?.practiceAreaConfigs || [];
    } catch (error) {
      console.error('Error getting all service area configs:', error);
      return [];
    }
  }

  /**
   * Update service area configuration.
   */
  async updatePracticeAreaConfig(
    practiceArea: string, 
    config: Partial<PracticeAreaNumberingConfig>
  ): Promise<void> {
    try {
      const firmSettings = await this.getFirmSettings();
      const existingConfigs = firmSettings.matterNumberingConfig?.practiceAreaConfigs || [];
      
      const existingIndex = existingConfigs.findIndex(pa => pa.practiceArea === practiceArea);
      const updatedConfigs = [...existingConfigs];
      
      if (existingIndex >= 0) {
        // Update existing config
        updatedConfigs[existingIndex] = {
          ...updatedConfigs[existingIndex],
          ...config
        };
      } else {
        // Add new config
        updatedConfigs.push({
          practiceArea,
          practiceAreaName: practiceArea,
          template: {
            id: 'professional-standard',
            name: 'Professional Standard',
            description: 'Default template',
            pattern: '{{YYYY}}-{{MM}}-{{DD}}-{{clientCode}}-{{practiceArea}}-{{sequence:3}}',
            category: 'professional',
            complexity: 3,
            examples: ['2025-01-15-SMITH-PI-001'],
            practiceAreas: 'all',
            status: 'enabled'
          },
          isEnabled: true,
          lastUpdatedBy: 'system',
          lastUpdatedDate: new Date().toISOString(),
          ...config
        });
      }

      // Update firm settings with new config
      const updatedFirmSettings = {
        ...firmSettings,
        matterNumberingConfig: {
          isEnabled: firmSettings.matterNumberingConfig?.isEnabled ?? true,
          defaultTemplate: firmSettings.matterNumberingConfig?.defaultTemplate ?? {
            id: 'professional-standard',
            name: 'Professional Standard',
            description: 'Default template',
            pattern: '{{YYYY}}-{{MM}}-{{DD}}-{{clientCode}}-{{practiceArea}}-{{sequence:3}}',
            category: 'professional',
            complexity: 3,
            examples: ['2025-01-15-SMITH-PI-001'],
            practiceAreas: 'all',
            status: 'enabled'
          },
          practiceAreaConfigs: updatedConfigs
        }
      };

      // Save updated firm settings
      await this.saveFirmSettings(updatedFirmSettings);
      
      // Update cache
      this.cachedFirmSettings = updatedFirmSettings;
    } catch (error) {
      console.error('Error updating service area config:', error);
      throw error;
    }
  }

  /**
   * Save subscriber settings through the WeBelieve system-preferences contract.
   */
  private async saveFirmSettings(firmSettings: FirmSettings): Promise<void> {
    try {
      const currentPreferences = await getSystemPreferences();
      await updateSystemPreferences({
        timezone: firmSettings.firmSettings.timezone,
        date_format: firmSettings.firmSettings.dateFormat,
        currency: firmSettings.firmSettings.currency,
        language: firmSettings.firmSettings.language,
        service_areas: this.serviceAreaNamesToPreferences(
          firmSettings.practiceAreas,
          currentPreferences.service_areas
        ),
        operating_contexts: this.operatingContextCodesToPreferences(
          firmSettings.licensedStates || [],
          currentPreferences.operating_contexts
        ),
      });
    } catch (error) {
      console.error('Error saving subscriber settings:', error);
      throw error;
    }
  }

  public toSystemPreferencesUpdate(firmSettings: FirmSettings): Partial<SystemPreferences> {
    return {
      timezone: firmSettings.firmSettings.timezone,
      date_format: firmSettings.firmSettings.dateFormat,
      currency: firmSettings.firmSettings.currency,
      language: firmSettings.firmSettings.language,
      service_areas: this.serviceAreaNamesToPreferences(firmSettings.practiceAreas),
      operating_contexts: this.operatingContextCodesToPreferences(firmSettings.licensedStates || []),
    };
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.cachedPracticeAreas = null;
    this.cachedFirmSettings = null;
    this.lastCacheTime = 0;
  }

  /**
   * Get default service areas (fallback).
   */
  private getDefaultPracticeAreas(): PracticeArea[] {
    return [
      { id: '1', name: 'operations', displayName: 'Operations', isActive: true },
      { id: '2', name: 'client-services', displayName: 'Client Services', isActive: true },
      { id: '3', name: 'sales-growth', displayName: 'Sales / Growth', isActive: true },
      { id: '4', name: 'finance-billing', displayName: 'Finance / Billing', isActive: true },
      { id: '5', name: 'people-hr', displayName: 'People / HR', isActive: true },
      { id: '6', name: 'projects-delivery', displayName: 'Projects / Delivery', isActive: true },
      { id: '7', name: 'support-success', displayName: 'Support / Success', isActive: true },
      { id: '8', name: 'governance-compliance', displayName: 'Governance / Compliance', isActive: true }
    ];
  }

  private async getAuthContext(): Promise<{ subscriber?: { subscriberId?: string; displayName?: string } } | null> {
    const token = (window as any).keycloak?.token
      || localStorage.getItem('webelieve_token')
      || sessionStorage.getItem('webelieve_token')
      || localStorage.getItem('token')
      || sessionStorage.getItem('token')
      || localStorage.getItem('webelieve_access_token');

    if (!token) {
      return null;
    }

    const response = await fetch('/api/v1/auth/context', {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      return null;
    }

    const result = await response.json();
    return result.data || null;
  }

  private mapSystemPreferencesToFirmSettings(
    preferences: SystemPreferences,
    authContext: { subscriber?: { subscriberId?: string; displayName?: string } } | null
  ): FirmSettings {
    const displayName = authContext?.subscriber?.displayName || authContext?.subscriber?.subscriberId || '';
    return {
      firmName: displayName,
      displayName,
      practiceAreas: (preferences.service_areas || [])
        .filter((area) => area.enabled !== false)
        .map((area) => area.label || area.code)
        .filter(Boolean),
      licensedStates: (preferences.operating_contexts || [])
        .filter((context) => context.enabled !== false && ['region', 'market'].includes(String(context.type || '')))
        .map((context) => context.code)
        .filter(Boolean),
      firmSettings: {
        timezone: preferences.timezone || 'America/New_York',
        dateFormat: preferences.date_format || 'MM/DD/YYYY',
        currency: preferences.currency || 'USD',
        language: preferences.language || 'en',
      },
    };
  }

  private serviceAreaNamesToPreferences(
    serviceAreaNames: string[],
    existingAreas: ServiceArea[] = []
  ): ServiceArea[] {
    const existingByLabel = new Map(
      existingAreas.map((area) => [String(area.label || area.code).toLowerCase(), area])
    );

    return serviceAreaNames
      .map((name) => name.trim())
      .filter(Boolean)
      .map((name) => {
        const existing = existingByLabel.get(name.toLowerCase());
        return {
          code: existing?.code || name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''),
          label: existing?.label || name,
          category: existing?.category || 'operations',
          enabled: true,
          extension: existing?.extension || 'base',
        };
      });
  }

  private operatingContextCodesToPreferences(
    contextCodes: string[],
    existingContexts: OperatingContext[] = []
  ): OperatingContext[] {
    const existingByCode = new Map(
      existingContexts.map((context) => [String(context.code).toUpperCase(), context])
    );

    return contextCodes
      .map((code) => code.trim().toUpperCase())
      .filter(Boolean)
      .map((code) => {
        const existing = existingByCode.get(code);
        return {
          type: existing?.type || 'region',
          code,
          label: existing?.label || code,
          enabled: true,
          extension: existing?.extension || 'base',
        };
      });
  }

  /**
   * Get service area display name by name.
   */
  async getPracticeAreaDisplayName(practiceAreaName: string): Promise<string> {
    try {
      const practiceAreas = await this.getAvailablePracticeAreas();
      const practiceArea = practiceAreas.find(pa => pa.name === practiceAreaName);
      return practiceArea?.displayName || practiceAreaName;
    } catch (error) {
      console.error('Error getting service area display name:', error);
      return practiceAreaName;
    }
  }

  /**
   * Validate service area configuration.
   */
  validatePracticeAreaConfig(config: PracticeAreaNumberingConfig): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!config.practiceArea) {
      errors.push('Service area is required');
    }

    if (!config.template) {
      errors.push('Template is required');
    }

    // Priority validation removed - not part of current type

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

// Export singleton instance
export const practiceAreaConfigService = PracticeAreaConfigService.getInstance(); 
